import React, { useState } from "react";
import {
    Settings,
    Menu,
    LayoutDashboard,
    Flag,
    Users,
    Link2,
    Wrench,
    LogOut as IconLogOut,
    ChevronsLeft,
    ChevronsRight,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useWorkspace } from "@/context/WorkspaceContext";
import WorkspaceCompanyDropdown from "@/components/sidebar/WorkspaceCompanyDropdown";
import { Workspace, WorkspaceCompany } from "@/types/workspace";
import CreateWorkspaceDialog from "@/components/sidebar/CreateWorkspaceDialog";
import { createWorkspace } from "@/services/workspaceService";
import { toast } from "sonner";
import { Avatar } from "@/components/vb";
import {
    buildCreditPurchaseReference,
    redirectToStripeWithReturnPath,
} from "@/lib/stripeNavigation";
import { useActiveCompany } from "@/hooks/useActiveCompany";
import { useWorkbenchQueue } from "@/hooks/workbench/useWorkbench";
import { isOutstanding } from "@/pages/workbench/workbenchUtil";

import { Link, useNavigate, useLocation } from "react-router-dom";
import IssueDialog from "@/components/widgets/IssueDialog";

export interface NavItem {
    key: string;
    label: string;
    icon: React.ReactNode;
    path?: string;
    isGroup?: boolean;
}

// Home is gone (AITS-540) — `/` is a resolver that redirects into a company,
// so there is no screen for a "Home" entry to point at. Dashboard is the
// landing surface now.
const NAV_ITEMS: NavItem[] = [
    // Dashboard — the active company's latest AI Trust Signal report.
    { key: "dashboard", label: "Dashboard", icon: <LayoutDashboard size={18} /> },
    // Workbench (MW-4) — the "do the work" view: the active company's fix queue
    // grounded in the Score Accelerators. Company-scoped like Dashboard / Sources.
    { key: "workbench", label: "Workbench", icon: <Wrench size={18} /> },
    { key: "virtual-buyer", label: "Virtual Buyer", icon: <Users size={18} />, path: "/virtual-buyer" },
    // Sources (RGS-5) — context-aware like Virtual Buyer: carries the active
    // company forward to its latest report's Sources page.
    { key: "sources", label: "Sources", icon: <Link2 size={18} /> },
];

const STRIPE_ONE_TIME_CREDIT_PURCHASE_URL = import.meta.env.VITE_STRIPE_ONE_TIME_CREDIT_PURCHASE;

type Props = {
    onOpenReportDialog: (prefill?: { name?: string; url?: string }) => void;
    collapsed: boolean;
    onCollapsedChange: (collapsed: boolean) => void;
    className?: string;
};

const Sidebar: React.FC<Props> = ({
                                      onOpenReportDialog,
                                      collapsed,
                                      onCollapsedChange,
                                      className = "",
                                  }) => {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [issueDialogOpen, setIssueDialogOpen] = useState(false);
    const [createWorkspaceOpen, setCreateWorkspaceOpen] = useState(false);
    const [createWorkspaceLoading, setCreateWorkspaceLoading] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();
    const {
        workspaces,
        activeWorkspace,
        billingAccount,
        loading: workspaceLoading,
        hasPaidAccess,
        setActiveWorkspace,
        refreshWorkspaces,
    } = useWorkspace();
    const isMobile = useIsMobile();

    const effectiveCollapsed = !isMobile && collapsed;

    const goto = (path: string) => {
        navigate(path);
        setDrawerOpen(false);
    };

    const handleLogout = () => {
        logout();
        navigate("/login");
    };

    const reportPath = (c: WorkspaceCompany) => `/reports/${c.company_id}`;
    // Picking a company always opens its dashboard — including one with no
    // report yet, because that page is where you run the first one. It used to
    // divert to the buyer list, which left no way in, and to open the progress
    // dialog without navigating, which left you on whatever page you were on.
    // The dashboard handles both states itself now: it auto-opens the progress
    // dialog while a first report builds, and shows the "generate this month"
    // prompt when there's nothing yet.
    const handleCompanyClick = (c: WorkspaceCompany) => {
        goto(reportPath(c));
    };

    const handleWorkspaceCompanyClick = (workspace: Workspace, company: WorkspaceCompany) => {
        if (!activeWorkspace || activeWorkspace.id !== workspace.id) {
            setActiveWorkspace(workspace);
        }
        handleCompanyClick(company);
    };

    const handleAddCompany = (workspace?: Workspace) => {
        if (workspace && (!activeWorkspace || activeWorkspace.id !== workspace.id)) {
            setActiveWorkspace(workspace);
        }
        onOpenReportDialog();
    };

    // The selector "+" runs a new report for the company currently shown in the
    // selector — so it opens the same dialog with that company prefilled. Falls
    // back to a blank form (add-a-company) when nothing is selected. Switch to
    // the workspace that owns the company first, since the dialog seeds its
    // workspace choice from the active one.
    const handleAddReport = (company: WorkspaceCompany | null) => {
        if (!company) {
            onOpenReportDialog();
            return;
        }
        const owning = workspaces.find((ws) =>
            ws.companies.some((c) => c.company_id === company.company_id),
        );
        if (owning && (!activeWorkspace || activeWorkspace.id !== owning.id)) {
            setActiveWorkspace(owning);
        }
        onOpenReportDialog({
            name: company.company_name,
            url: company.company_url ?? undefined,
        });
    };

    const handleAddWorkspace = () => {
        setCreateWorkspaceOpen(true);
    };

    const handleCreateWorkspace = async (workspaceName: string) => {
        try {
            setCreateWorkspaceLoading(true);
            const createdWorkspace = await createWorkspace(workspaceName);
            await refreshWorkspaces();
            setActiveWorkspace(createdWorkspace);
            setCreateWorkspaceOpen(false);
            onOpenReportDialog();
            toast.success("Workspace created");
        } catch (error) {
            const err = error as Error & { code?: string; onboardingRequired?: boolean };
            if (err.code === "billing_onboarding_required" || err.onboardingRequired) {
                setCreateWorkspaceOpen(false);
                navigate("/onboarding", {
                    replace: true,
                    state: { onboardingNotice: "You need to onboard yourself first." },
                });
                return;
            }

            const message = err.message || "Failed to create workspace";
            toast.error(message);
        } finally {
            setCreateWorkspaceLoading(false);
        }
    };

    // Active company drives the dropdown highlight and every company-scoped nav
    // target. Resolved centrally (AITS-540) so it survives the routes that don't
    // carry a company — Settings above all, which used to grey the whole nav out.
    const activeCompanyId = useActiveCompany();

    // Live "issues to fix" count for the active company's Workbench. Shares the
    // WorkbenchPage's React Query cache (same key), so it decrements as fixes get
    // marked done. Paid-only, and only when a company is active; a company with no
    // paid workbench report 404s → no data → count 0 → no badge shown.

    // Workbench and Sources both read a report, so they're disabled for a
    // company that has never had one rather than leading to an empty screen.
    //
    // Keyed on "no report at all", not on the latest report's status: `status`
    // reads "in_progress" during every monthly re-run, which would grey both out
    // for companies that do have history. The residual gap — a company whose
    // FIRST run is still in flight keeps both enabled — needs a
    // "has a completed report" flag from /api/user-companies/ to close, and
    // meanwhile both pages show their own empty state rather than breaking.
    const activeCompany = activeWorkspace?.companies?.find(
        (c) => c.company_id === activeCompanyId,
    );
    const companyHasReport = activeCompany ? activeCompany.lastReportId != null : true;

    const workbenchQuery = useWorkbenchQueue(
        activeCompanyId ?? undefined,
        hasPaidAccess && activeCompanyId != null,
    );
    // Derive the count from `fixes` — outstanding = open OR regressed (a fix
    // marked done that the latest report still flags). Same source the queue and
    // progress ring use, so marking a fix done decrements it live (the mutation
    // patches fixes[].task, not the server-sent progress.remaining).
    const workbenchReportDate = workbenchQuery.data?.report?.created_at ?? null;
    const workbenchCount =
        workbenchQuery.data?.fixes?.filter((f) => isOutstanding(f, workbenchReportDate)).length ??
        0;
    // Badge tone follows the worst OUTSTANDING severity — red if any critical fix
    // is unresolved, amber otherwise. Text colour is chosen for contrast on each
    // (white reads on red ~4.5:1; on amber only dark text passes ~9.8:1).
    const workbenchHasCritical = !!workbenchQuery.data?.fixes?.some(
        (f) => isOutstanding(f, workbenchReportDate) && f.severity === "critical",
    );

    const desktopWidthClass = effectiveCollapsed ? "sm:w-16" : "w-[268px]";
    const isSettingsActive = location.pathname.startsWith("/settings");

    const isRouteActive = (item: NavItem) =>
        item.key === "sources"
            ? location.pathname.endsWith("/sources")
            : item.key === "workbench"
                ? location.pathname.endsWith("/workbench")
                : item.key === "dashboard"
                    // Any report route that isn't the Sources / Workbench sub-view.
                    ? location.pathname.startsWith("/reports/") &&
                      !location.pathname.endsWith("/sources") &&
                      !location.pathname.endsWith("/workbench")
                    : item.path
                        ? location.pathname.startsWith(item.path)
                        : false;

    // Every entry is company-scoped and carries the selected company forward, so
    // opening one from a company context keeps that company scoped (and the
    // selector highlighted). `activeCompanyId` now falls back to the remembered
    // / newest-reported company (AITS-540), so these stay live on the
    // workspace-scoped screens; they only go dark for an account that has no
    // company at all, where there is genuinely nowhere to go.
    const navTarget = (item: NavItem): string | undefined => {
        if (item.key === "dashboard") {
            // `/reports/:companyId` resolves to that company's newest report
            // (same target as clicking the company in the dropdown).
            return activeCompanyId ? `/reports/${activeCompanyId}` : undefined;
        }
        if (item.key === "workbench") {
            return activeCompanyId && companyHasReport
                ? `/reports/${activeCompanyId}/workbench`
                : undefined;
        }
        if (item.key === "virtual-buyer") {
            return activeCompanyId ? `/virtual-buyer?company=${activeCompanyId}` : undefined;
        }
        if (item.key === "sources") {
            return activeCompanyId && companyHasReport
                ? `/reports/${activeCompanyId}/sources`
                : undefined;
        }
        return item.path;
    };

    const billingCredits = (billingAccount?.credits ?? 0) + (billingAccount?.purchased_credits ?? 0);
    const userDisplayName = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.email || "User";
    // Initials + tone come from the shared Avatar, so this chip cannot drift from
    // the same person's mark in settings (it used to: two letters here, one there).
    const initialsSource = [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() || user?.email || "User";
    const isSubscribedUser = hasPaidAccess;
    const buyCreditsEnabled = Boolean(STRIPE_ONE_TIME_CREDIT_PURCHASE_URL) && isSubscribedUser;

    const handleBuyCredits = () => {
        if (!buyCreditsEnabled) {
            return;
        }

        redirectToStripeWithReturnPath(STRIPE_ONE_TIME_CREDIT_PURCHASE_URL, {
            email: user?.email,
            returnPath: `${location.pathname}${location.search}${location.hash}`,
            clientReferenceId: buildCreditPurchaseReference(
                activeWorkspace?.id ?? null,
                "sidebar",
            ),
        });
    };

    return (
        <>
            <div className="w-screen border-b border-[color:var(--vb-line-strong)] bg-[var(--vb-sidebar)] sm:hidden">
                <button
                    className="inline-flex items-center p-2 mt-2 ms-3 text-sm text-[color:var(--vb-text-2)] rounded-lg hover:bg-[var(--vb-surface)] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-vb-focus"
                    onClick={() => setDrawerOpen((open) => !open)}
                    aria-label="Toggle sidebar"
                >
                    <Menu className="w-6 h-6" />
                </button>
            </div>

            {drawerOpen && (
                <div className="fixed inset-0 bg-[var(--vb-scrim)] z-30 sm:hidden" onClick={() => setDrawerOpen(false)} />
            )}

            <aside
                className={[
                    "z-40 transition-transform border-r border-[color:var(--vb-line-strong)] bg-[var(--vb-sidebar)]",
                    isMobile ? "fixed top-0 left-0 h-[100dvh]" : "h-full shrink-0",
                    drawerOpen ? "translate-x-0" : "-translate-x-full",
                    "sm:translate-x-0",
                    desktopWidthClass,
                    className
                ].join(" ")}
            >
                <div className={["h-full flex flex-col overflow-y-auto text-[color:var(--vb-text)] pt-8 pb-4", effectiveCollapsed ? "px-2" : "px-4"].join(" ")}>
                    <div className={`mb-4 flex items-center justify-between ${effectiveCollapsed ? "flex-col" : "flex-row"}`}>
                        <Link to="/" aria-label="Go to your dashboard" className={effectiveCollapsed ? "mb-2" : ""}>
                            {effectiveCollapsed ? (
                                <img src="/AITrustSignalLogoBlue.svg" alt="AI Trust Signals" className="h-8 w-8" />
                            ) : (
                                <img src="/AITrustSignalSideBarLogo.svg" alt="AI Trust Signals" className="h-6" />
                            )}
                        </Link>

                        <button
                            onClick={() => onCollapsedChange(!collapsed)}
                            className="hidden sm:inline-flex items-center justify-center rounded-md p-1 text-[color:var(--vb-text-2)] hover:bg-[var(--vb-surface)]"
                            aria-label={effectiveCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                            title={effectiveCollapsed ? "Expand" : "Collapse"}
                        >
                            {effectiveCollapsed ? <ChevronsRight size={18} /> : <ChevronsLeft size={18} />}
                        </button>
                    </div>

                    <nav className="flex-1">
                        <WorkspaceCompanyDropdown
                            workspaces={workspaces}
                            activeWorkspace={activeWorkspace}
                            activeCompanyId={activeCompanyId}
                            loading={workspaceLoading}
                            onCompanyClick={handleWorkspaceCompanyClick}
                            onAddCompany={handleAddCompany}
                            onAddReport={handleAddReport}
                            onAddWorkspace={handleAddWorkspace}
                            collapsed={effectiveCollapsed}
                        />

                        {NAV_ITEMS.map((item) => {
                            const active = isRouteActive(item);
                            const target = navTarget(item);
                            const disabled = !target;
                            // "Issues to fix" count on the Workbench item — only when there's work.
                            const badgeCount = item.key === "workbench" && workbenchCount > 0 ? workbenchCount : 0;
                            return (
                                <button
                                    key={item.key}
                                    disabled={disabled}
                                    onClick={() => {
                                        if (target) goto(target);
                                    }}
                                    title={
                                        disabled
                                            ? activeCompanyId
                                                ? "Run a report for this company first"
                                                : "Add a company first"
                                            : effectiveCollapsed
                                                ? `${item.label}${badgeCount ? ` — ${badgeCount} to fix` : ""}`
                                                : undefined
                                    }
                                    className={[
                                        "relative flex items-center w-full rounded-md text-sm hover:bg-[var(--vb-surface)]",
                                        effectiveCollapsed ? "justify-center h-10 mb-1" : "px-2 py-2 mb-1",
                                        active ? "bg-[var(--vb-brand-soft)] text-[color:var(--vb-brand-pressed)]" : "",
                                        disabled ? "opacity-40 cursor-not-allowed hover:bg-transparent" : "",
                                    ].join(" ")}
                                >
                                    {item.icon}
                                    {!effectiveCollapsed && <span className="ms-3">{item.label}</span>}
                                    {badgeCount > 0 && (
                                        <span
                                            aria-label={`${badgeCount} issues to fix`}
                                            className={[
                                                "inline-flex items-center justify-center rounded-full font-bold leading-none tabular-nums",
                                                effectiveCollapsed
                                                    ? "absolute top-1 right-1 min-w-[16px] h-4 px-1 text-[10px]"
                                                    : "ms-auto min-w-[20px] h-5 px-1.5 text-[11px]",
                                            ].join(" ")}
                                            style={
                                                workbenchHasCritical
                                                    ? { backgroundColor: "var(--vb-fail)", color: "var(--vb-on-brand)" }
                                                    : { backgroundColor: "var(--vb-warn)", color: "var(--vb-warn-ink)" }
                                            }
                                        >
                                            {badgeCount}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </nav>

                    <div className="mt-auto space-y-2">

                        {!effectiveCollapsed && (
                            <div className="mb-2 rounded-xl border border-[color:var(--vb-line)] bg-[var(--vb-surface)] px-2.5 py-2">
                                <div className="flex items-center gap-2.5">
                                    <Avatar
                                        size="lg"
                                        round
                                        kind="user"
                                        name={initialsSource}
                                        className="h-8 w-8 border border-[color:var(--vb-line)] text-xs"
                                        decorative
                                    />
                                    <div className="min-w-0">
                                        <p className="truncate text-sm font-semibold leading-5 text-[color:var(--vb-text)]">
                                            {userDisplayName}
                                        </p>
                                        <p className="text-xs text-[color:var(--vb-text-3)]">
                                            {billingCredits} credits
                                            {buyCreditsEnabled ? (
                                                <>
                                                    <span className="mx-1">·</span>
                                                    <button
                                                        type="button"
                                                        className="font-medium text-accent hover:underline"
                                                        onClick={handleBuyCredits}
                                                    >
                                                        Buy
                                                    </button>
                                                </>
                                            ) : null}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                        <button
                            onClick={() => goto("/settings?tab=workspace")}
                            title="Settings"
                            className={[
                                "w-full flex items-center gap-2 rounded hover:bg-[var(--vb-surface)]",
                                effectiveCollapsed ? "justify-center h-10" : "px-2 py-2 text-sm",
                                isSettingsActive ? "bg-[var(--vb-brand-soft)] text-[color:var(--vb-brand-pressed)]" : "",
                            ].join(" ")}
                        >
                            <Settings size={16} />
                            {!effectiveCollapsed && <span>Settings</span>}
                        </button>

                        <button
                            onClick={() => setIssueDialogOpen(true)}
                            title="Report an issue"
                            className={[
                                "w-full flex items-center gap-2 rounded hover:bg-[var(--vb-surface)]",
                                effectiveCollapsed ? "justify-center h-10" : "px-2 py-2 text-sm",
                            ].join(" ")}
                        >
                            <Flag size={16} />
                            {!effectiveCollapsed && <span>Report an issue</span>}
                        </button>

                        <button
                            onClick={handleLogout}
                            title="Log out"
                            className={[
                                "w-full flex items-center gap-2 rounded hover:bg-[var(--vb-fail-bg)]",
                                effectiveCollapsed ? "justify-center h-10 text-[color:var(--vb-fail-ink)]" : "px-2 py-2 text-sm text-[color:var(--vb-fail-ink)]",
                            ].join(" ")}
                        >
                            <IconLogOut size={16} />
                            {!effectiveCollapsed && <span>Log out</span>}
                        </button>
                    </div>
                    <div className="flex items-center gap-1 text-sm text-[color:var(--vb-text-3)] mt-8 justify-center">
                        {!effectiveCollapsed && <span>Created by</span>}
                        <a
                            href="https://maiven.io"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center"
                            title={effectiveCollapsed ? "Created by Maiven" : undefined}
                        >
                            <img alt="logo" src="/maivenLogo.png" className="filter brightness-0" />
                        </a>
                    </div>
                </div>
            </aside>

            <IssueDialog open={issueDialogOpen} setOpen={setIssueDialogOpen} />
            <CreateWorkspaceDialog
                open={createWorkspaceOpen}
                onOpenChange={setCreateWorkspaceOpen}
                onCreate={handleCreateWorkspace}
                loading={createWorkspaceLoading}
            />
        </>
    );
};

export default Sidebar;
